import FacebookIcon from "@src/app/assets/icons/facebook.svg?react";
import ShareIcon from "@src/app/assets/icons/share.svg?react";
import WechatIcon from "@src/app/assets/icons/wechat.svg?react";
import WhatsappIcon from "@src/app/assets/icons/whatsapp.svg?react";
import XIcon from "@src/app/assets/icons/x.svg?react";
import Button from "@src/app/components/Button/Button";
import useGTMDispatch from "@src/app/hooks/useGTMDispatch";
import viewportState from "@src/app/stores/viewport";
import cx from "classnames";
import type { ReactElement } from "react";
import { useEffect, useRef, useState } from "react";
import type { LinksFunction } from "react-router";

import CopyLinkButton from "./CopyLinkButton";
import { nativeShare, ShareToastContainer } from "./shareToastHelper";
import styles from "./SocialShare.css?url";
import SocialShareButton from "./SocialShareButton";
export const SocialShareLinks: LinksFunction = () => [
  { rel: "stylesheet", href: styles },
];

interface WindowWithA2A extends Window {
  a2a?: {
    init: (type: string) => void;
  };
}

type Props = Readonly<{
  isVideoArticle?: boolean;
  articleTitle?: string;
  articleUrl?: string;
  youtubeTitle?: string;
  className?: string;
  style?: React.CSSProperties;
  viewType: "inline" | "dropdown";
  theme?: "light" | "dark";
  enabledCopyLink?: boolean;
}>;

export default function SocialShare({
  isVideoArticle,
  articleTitle,
  articleUrl,
  youtubeTitle,
  className,
  style,
  viewType = "inline",
  theme = "light",
  enabledCopyLink = true,
}: Props): ReactElement {
  const [isSocialShareVisible, setIsSocialShareVisible] = useState(false);
  const [hasCopiedLink, setHasCopiedLink] = useState(false);
  const socialWrapperRef = useRef<HTMLDivElement>(null);
  const { isMobileOrTabletDevice } = viewportState;
  const a2aClasses = "a2a_kit a2a_kit_size_32";
  const articlePath =
    typeof window !== "undefined" ? window.location.origin + articleUrl : "";
  const dataAttributes = isVideoArticle
    ? {
        "data-a2a-url": encodeURIComponent(articlePath),
        "data-a2a-title": youtubeTitle
          ? encodeURIComponent(youtubeTitle)
          : undefined,
      }
    : {};

  // Safe SSR title & url
  const safeTitle = articleTitle
    ? encodeURIComponent(articleTitle)
    : typeof document !== "undefined"
      ? (() => {
          const ogTitle = document
            .querySelector('meta[property="og:title"]')
            ?.getAttribute("content");
          return encodeURIComponent(ogTitle || document.title || "");
        })()
      : "";

  const safeUrl = articleUrl
    ? encodeURIComponent(articlePath)
    : typeof window !== "undefined"
      ? encodeURIComponent(window.location.href)
      : "";

  // Toggle social share dropdown visibility
  const toggleDropdown = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setIsSocialShareVisible((prev) => !prev);
    if (hasCopiedLink) {
      setHasCopiedLink(false);
    }
  };

  // Handle click
  const handleClickSocialShare = (
    event: React.PointerEvent<HTMLButtonElement>,
  ) => {
    event.stopPropagation();

    if (isMobileOrTabletDevice) {
      nativeShare({
        title: safeTitle,
        text: safeTitle,
        url: safeUrl,
        autoClose: 3000,
        toastTitle: "Thanks for sharing",
        fallbackToastTitle: "Link Copied",
      });
      return;
    }
    toggleDropdown(event);
  };

  // Handle click outside to close dropdown
  const handleClickOutside = (event: MouseEvent) => {
    if (
      socialWrapperRef.current &&
      !socialWrapperRef.current.contains(event.target as Node)
    ) {
      setIsSocialShareVisible(false);
    }
  };

  const sendDataToGTM = useGTMDispatch();
  const handleClickGTM = (mediaType: string) => {
    sendDataToGTM({
      event: "tcCustomEvent",
      eventCategory: "social sharing",
      eventAction: "click",
      eventLabel: mediaType,
    });
  };

  const hideA2AModal = () => {
    if (typeof document == "undefined") return;

    const observer = new MutationObserver(() => {
      const overlay = document.querySelector(".a2a_overlay");
      if (!overlay) return;

      const displayStyle = getComputedStyle(overlay).display;
      if (displayStyle && displayStyle !== "none") {
        // stop observing
        observer.disconnect();
        setTimeout(() => {
          // click to trigger close the modals
          (overlay as HTMLElement).click();
        }, 3000);
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });
  };

  const resetSocialShareVisibility = () => {
    setIsSocialShareVisible(false);
    hideA2AModal();
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    document.addEventListener("click", handleClickOutside, true);
    return () => {
      document.removeEventListener("click", handleClickOutside, true);
    };
  }, []);

  // Load AddToAny script dynamically only when needed
  useEffect(() => {
    if (!document.getElementById("addtoany-script")) {
      (window as WindowWithA2A & { a2a_config?: unknown }).a2a_config = {
        locale: "custom",
        localize: {
          ThanksForSharing: "Thanks for sharing",
        },
        thanks: {
          postShare: true,
          ad: false,
        },
      };

      const script = document.createElement("script");
      script.id = "addtoany-script";
      script.src = "https://static.addtoany.com/menu/page.js";
      script.async = true;
      document.body.appendChild(script);

      script.onload = () => {
        const windowWithA2A = window as WindowWithA2A;
        windowWithA2A.a2a?.init("page");
        hideA2AModal();
      };
    } else {
      (window as WindowWithA2A).a2a?.init("page");
      hideA2AModal();
    }
  }, []);
  return (
    <div
      data-testid="social-share-button"
      className={cx(
        "flex flex-row",
        viewType === "dropdown" ? "gap-3" : "gap-2",
        className,
      )}
      style={style}
    >
      <div className="relative">
        {viewType === "dropdown" && (
          <Button
            variant={
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              viewType === "dropdown" && theme === "light"
                ? "tertiary"
                : "secondary"
            }
            size="medium"
            icon={<ShareIcon width={20} height={20} />}
            style={{
              touchAction: "manipulation",
            }}
            className="cursor-pointer"
            {...(isMobileOrTabletDevice
              ? { onPointerUp: handleClickSocialShare }
              : { onClick: handleClickSocialShare })}
          />
        )}

        {!isMobileOrTabletDevice && (
          <div
            ref={socialWrapperRef}
            className={cx(
              a2aClasses,
              "z-50 flex w-auto gap-2",

              {
                // list view type
                "flex-row": viewType === "inline",

                // drop down view type
                "bg-background-default text-button-primary-label animate-fade-in absolute -top-2 -left-2 flex-col gap-2 rounded-full p-2 shadow-md":
                  viewType === "dropdown",

                // Hide/show dropdown
                hidden: viewType === "dropdown" && !isSocialShareVisible,
                block: viewType === "dropdown" && isSocialShareVisible,
              },
            )}
            data-testid="social-share-dropdown"
            {...dataAttributes}
          >
            <SocialShareButton
              theme={viewType === "dropdown" ? "dark" : theme}
              icon={<FacebookIcon width={20} height={20} />}
              mediaType="facebook"
              onClick={() => {
                resetSocialShareVisibility();
                handleClickGTM("facebook");
              }}
            />
            <SocialShareButton
              theme={viewType === "dropdown" ? "dark" : theme}
              icon={<XIcon width={20} height={20} />}
              mediaType="twitter"
              onClick={() => {
                resetSocialShareVisibility();
                handleClickGTM("twitter");
              }}
            />
            <SocialShareButton
              theme={viewType === "dropdown" ? "dark" : theme}
              icon={<WechatIcon width={20} height={20} />}
              mediaType="wechat"
              onClick={() => {
                resetSocialShareVisibility();
                handleClickGTM("wechat");
              }}
            />
            <SocialShareButton
              theme={viewType === "dropdown" ? "dark" : theme}
              icon={<WhatsappIcon width={20} height={20} />}
              mediaType="whatsapp"
              onClick={() => {
                resetSocialShareVisibility();
                handleClickGTM("whatsapp");
              }}
            />
          </div>
        )}
      </div>

      <ShareToastContainer />

      {enabledCopyLink && (
        <CopyLinkButton
          isVideoArticle={isVideoArticle}
          articleUrl={articlePath}
          setHasCopiedLink={setHasCopiedLink}
          hasCopiedLink={hasCopiedLink}
          theme={theme}
        />
      )}
    </div>
  );
}
